import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Radio,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import { Expense } from "../../lib/types";
import { expenseUtils } from "../../lib";
import {
  getExpenseSeriesRootId,
  isRecurringSeriesExpense,
} from "../../lib/expenseSeriesUtils";

interface MergeExpensesDialogProps {
  open: boolean;
  candidates: Expense[];
  onConfirm: (opts: {
    survivorId: string;
    newDate: Date;
    makeRecurring: boolean;
    recurrencePattern: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}

const RECURRENCE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "bi-weekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semi-annually" },
  { value: "annually", label: "Annually" },
];

const toDateInputValue = (d: Date): string => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const MergeExpensesDialog: React.FC<MergeExpensesDialogProps> = ({
  open,
  candidates,
  onConfirm,
  onCancel,
}) => {
  const [survivorId, setSurvivorId] = useState<string | null>(null);
  const [dateValue, setDateValue] = useState<string>("");
  const [makeRecurring, setMakeRecurring] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState<string>("monthly");
  const [confirming, setConfirming] = useState(false);

  // Pick a sensible default survivor: the single recurring member, else earliest dated.
  const defaultSurvivor = useMemo(() => {
    if (candidates.length === 0) return null;
    const recurring = candidates.filter(isRecurringSeriesExpense);
    if (recurring.length === 1) return recurring[0];
    return [...candidates].sort(
      (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
    )[0];
  }, [candidates]);

  // Initialize state when the dialog opens / candidates change.
  useEffect(() => {
    if (!open || !defaultSurvivor) return;
    setSurvivorId(defaultSurvivor.id);
    setDateValue(toDateInputValue(new Date(defaultSurvivor.dueDate)));
    setMakeRecurring(isRecurringSeriesExpense(defaultSurvivor));
    setRecurrencePattern(defaultSurvivor.recurrencePattern || "monthly");
  }, [open, defaultSurvivor]);

  const survivor = useMemo(
    () => candidates.find((c) => c.id === survivorId) || null,
    [candidates, survivorId]
  );

  // Mismatch detection across the selection.
  const mismatches = useMemo(() => {
    const distinct = (key: (e: Expense) => string) =>
      Array.from(new Set(candidates.map(key).filter((v) => v && v.length > 0)));
    const names = distinct((e) => e.name || "");
    const vendors = distinct((e) => e.vendor || "");
    const roots = distinct((e) => getExpenseSeriesRootId(e));
    return {
      names: names.length > 1 ? names : [],
      vendors: vendors.length > 1 ? vendors : [],
      series: roots.length > 1,
    };
  }, [candidates]);

  const hasMismatch =
    mismatches.names.length > 0 ||
    mismatches.vendors.length > 0 ||
    mismatches.series;

  const dateInvalid = !dateValue || isNaN(new Date(dateValue).getTime());

  const handleConfirm = async () => {
    if (!survivorId || dateInvalid) return;
    setConfirming(true);
    try {
      // Parse as local date noon to avoid timezone day-shift.
      const newDate = new Date(`${dateValue}T12:00:00`);
      await onConfirm({
        survivorId,
        newDate,
        makeRecurring,
        recurrencePattern: makeRecurring ? recurrencePattern : null,
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (confirming) return;
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CallMergeIcon />
        Merge {candidates.length} Expenses
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The selected expenses will be combined into one. The survivor keeps
          its payment history, linked debt, and core fields; the others are
          absorbed and removed.
        </Typography>

        {hasMismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              These items don't all match — they'll still be combined into one.
            </Typography>
            {mismatches.names.length > 0 && (
              <div>Different names: {mismatches.names.join(", ")}</div>
            )}
            {mismatches.vendors.length > 0 && (
              <div>Different vendors: {mismatches.vendors.join(", ")}</div>
            )}
            {mismatches.series && (
              <div>Items belong to different recurring series.</div>
            )}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Keep which expense (survivor)?
        </Typography>
        <Paper
          variant="outlined"
          sx={{ maxHeight: 240, overflow: "auto", mb: 2 }}
        >
          <List dense disablePadding>
            {candidates.map((exp) => (
              <ListItemButton
                key={exp.id}
                selected={survivorId === exp.id}
                onClick={() => setSurvivorId(exp.id)}
                divider
              >
                <Radio
                  edge="start"
                  checked={survivorId === exp.id}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    color: "#805AD5",
                    "&.Mui-checked": { color: "#805AD5" },
                  }}
                />
                <ListItemText
                  primary={`${exp.name} — ${expenseUtils.formatCurrency(
                    exp.amount
                  )}`}
                  secondary={`${exp.vendor || "No vendor"} · due ${new Date(
                    exp.dueDate
                  ).toLocaleDateString()}${
                    isRecurringSeriesExpense(exp) ? " · recurring" : ""
                  }`}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <TextField
          label="New date"
          type="date"
          fullWidth
          size="small"
          value={dateValue}
          onChange={(e) => setDateValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={makeRecurring}
              onChange={(e) => setMakeRecurring(e.target.checked)}
              sx={{
                color: "#805AD5",
                "&.Mui-checked": { color: "#805AD5" },
              }}
            />
          }
          label="This merged expense recurs"
        />

        {makeRecurring && (
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              label="Recurrence"
              fullWidth
              size="small"
              value={recurrencePattern}
              onChange={(e) => setRecurrencePattern(e.target.value)}
            >
              {RECURRENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              All future instances will be deleted and rebuilt from the new date
              and cadence.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={confirming}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!survivor || dateInvalid || confirming}
          startIcon={
            confirming ? <CircularProgress size={16} /> : <CallMergeIcon />
          }
          onClick={handleConfirm}
          sx={{
            backgroundColor: "#805AD5",
            "&:hover": { backgroundColor: "#6B46C1" },
          }}
        >
          {confirming ? "Merging..." : "Merge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeExpensesDialog;
